import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  toggleId,
  getKeyValues,
  isPresentInArray,
  checkSubsetExistInArray,
  search,
  buildOptions
} from '../HelperFunctions'
import { dataLoad } from '../../redux/actions/autoCompleteActions'

let delayTimer

const OptionRow = React.memo(({ item, checked, onClick, textField, beforeLabel, disabled }) => {
  if (item.id === 'self_pay') {
    return (
      <div 
        className={`dropdown-item px-4 py-2 pointer d-flex align-items-center ${checked ? 'text-primary' : ''}`}
        style={checked ? { backgroundColor: '#eaf4ff' } : {}}
        onClick={onClick}
      >
        <i className="las la-user mr-2 font-size-18" />
        {item[textField]}
      </div>
    )
  }

  return (
    <div
      className={`custom-control custom-checkbox mx-3 ${disabled ? 'disabled text-muted' : ''}`}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick(e);
      }}
    >
      <input type="checkbox" className="custom-control-input" checked={checked} readOnly disabled={disabled} />
      <label className={`custom-control-label ${beforeLabel && 'd-flex align-items-center'}`}>
        {beforeLabel ? beforeLabel(item) : ''}
        {item[textField]} {item.is_archived ? '(Archived)' : ''}
      </label>
    </div>
  );
})

export default function MultiSelectWithSelfPayField(props) {
  const { input, meta: { touched, error, warning }, label, data, valueField, textField, placeholder, klass, rpp = 50, filter, isSearchVisible, isAllSelectVisible, dropdownSearchPlaceholder, dropdownClasses, wrapperClasses, beforeLabel, disabled, loading, showAllSelected, onClickOutside, supportOptimizedLoad = false, onSelectHook, readOnly } = props
  const selectedItemIds = input.value || []

  const dispatch = useDispatch()
  const nodeRef = useRef(null)
  const dropdownRef = useRef(null)

  const [options, setOptions] = useState([]) // List of options
  const [visible, setVisible] = useState(false)
  const [searchVal, setSearchVal] = useState('')
  const [loadState, setLoadState] = useState({ loading: false, loadedOnce: false, page: 1, hasMore: true })

  const baseOptions = useMemo(() => buildOptions(data, textField, valueField), [data, textField, valueField])
  const optionIds = useMemo(() => getKeyValues(options, valueField), [options, valueField])
  const dataKeysString = useMemo(() => JSON.stringify(getKeyValues(data || [], valueField)), [data, valueField])

  const updateSelection = useCallback(
    (newIds) => {
      input.onChange(newIds)
      if (onSelectHook) onSelectHook(newIds)
    },
    [input, onSelectHook]
  )

  const handleChecked = useCallback(
    (id) => {
      if (readOnly) return;
      const newIds = toggleId(selectedItemIds, id)
      updateSelection(newIds)
    },
    [selectedItemIds, updateSelection]
  )
  const handleRemoveSelected = (e) => {
    e.stopPropagation();
    updateSelection([]);
  }

  const loadOptions = useCallback(
    (append = false, page = 1, search = '') => {
      if (!klass) return
      setLoadState(s => ({ ...s, loading: true }))
      const extraFilter = filter ?? {}
      dispatch(dataLoad({ klass, rpp, page, search, ...extraFilter }))
        .then(res => {
          const records = res.value.data.data
          setOptions(prev => append ? [...prev, ...records] : records)
          setLoadState(s => ({
            ...s,
            loading: false,
            loadedOnce: true,
            page,
            hasMore: records.length === rpp
          }))
          if (showAllSelected && records.length && !append) {
            const allIds = getKeyValues(records, valueField)
            updateSelection(allIds)
          }
        })
        .finally(() => setLoadState(s => ({ ...s, loading: false })))
    },
    [klass, rpp, dispatch, filter, updateSelection, showAllSelected, valueField]
  )

  useEffect(() => {
    // Initial load
    if (!supportOptimizedLoad) {
      if (klass && !data?.length) loadOptions(false, 1)
      else setOptions(baseOptions)
    } else {
      setOptions(baseOptions)
    }
    // }, [supportOptimizedLoad, klass, data, baseOptions, loadOptions])
  }, [])

  useEffect(() => {
    if (data !== undefined) {
      setOptions(baseOptions)
    }
  }, [dataKeysString])

  useEffect(() => {
    // Outside click close
    const handleOutside = e => {
      if (nodeRef.current?.contains(e.target)) return
      setVisible(false)
      if (onClickOutside) onClickOutside()
    }
    if (visible) document.addEventListener('click', handleOutside)
    return () => document.removeEventListener('click', handleOutside)
  }, [visible, onClickOutside])

  const handleToggle = () => {
    setVisible(v => {
      const next = !v
      if (next && supportOptimizedLoad && klass && !loadState.loadedOnce) {
        loadOptions(false, 1)
      }
      return next
    })
  }

  const handleAllChecked = useCallback(() => {
    if (readOnly) return;
    let newIds
    if (checkSubsetExistInArray(optionIds, selectedItemIds)) newIds = []
    else newIds = [...new Set([...selectedItemIds, ...optionIds])]
    updateSelection(newIds)
  }, [optionIds, selectedItemIds, updateSelection])

  const handleUserSearch = e => {
    if (readOnly) return;
    const val = e.target.value
    setSearchVal(val)

    if (data?.length) {
      setOptions(search(baseOptions, textField, val))
    } else if (klass) {
      clearTimeout(delayTimer)
      delayTimer = setTimeout(() => {
        loadOptions(false, 1, val)
      }, 500)
    }
  }

  const handleScroll = e => {
    // Infinite scroll
    if (!loadState.hasMore || loadState.loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      loadOptions(true, loadState.page + 1, searchVal)
    }
  }

  const placeholderView = (isToolTip) => {
    const dataKeys = getKeyValues(
      data?.length ? data : options,
      valueField
    )

    // Show "Self Pay" whenever self_pay is selected
    if (selectedItemIds.includes('self_pay')) {
      return <span>Self Pay</span>
    }

    if (selectedItemIds.length > 0) {
      if (selectedItemIds.length === dataKeys.length) {
        return <span>All Selected</span>
      }

      const source = data?.length ? baseOptions : options
      const first = source.find(
        (o) => selectedItemIds[0] === o[valueField]
      )

      if (!first) return null

      if (selectedItemIds.length === 1) {
        return <span>{first[textField]}</span>
      }

      return (
        <span>
          <span>{first[textField]}</span>
          <span
            className={`pl-3 ${
              !isToolTip &&
              'text-muted label-increase bg-white position-sticky'
            }`}
          >
            (+{selectedItemIds.length - 1})
          </span>
        </span>
      )
    }

    return <span className="place-visible">{placeholder}</span>
  }
  return (
    <div className={`${disabled && 'disabled'}  ${touched && error && 'is-invalid'} ${wrapperClasses}`}>
      {label && <label className={`${loading && 'disabled'}`}>{label}</label>}
      <div
        className={`dropdown border position-relative rounded form-control ${(disabled || readOnly) ? 'bg-light' : ''}`}
        ref={nodeRef}
      >
        <span className="pointer selected overflow-hidden dropdown-toggle d-flex align-items-center" onClick={handleToggle}>
          <span className="overflow-hidden mr-5 autocomplete-tooltip w-100">
            {placeholderView()}
            {selectedItemIds.length !== 0 && <span className="tooltiptext">{placeholderView(true)}</span>}
          </span>
          {selectedItemIds.length > 0 && !readOnly && (
            <i className="la la-times-circle text-right text-muted reset-close pointer" title="Clear selected insurance" onClick={handleRemoveSelected} />
          )}
          <span className="la la-angle-down pl-4 bg-white icon-position" />
        </span>

        <div
          ref={dropdownRef}
          onScroll={handleScroll}
          className={`dropdown-menu ${dropdownClasses} overflow-auto w-100 px-5 ${visible && "show"}`}
          style={{ maxHeight: '20rem' }}
        >
          {isSearchVisible && (
            <div className="input-group mb-5">
              <input
                value={searchVal}
                onChange={handleUserSearch}
                type="text"
                className="form-control form-control-sm"
                placeholder={dropdownSearchPlaceholder || "Type to search"}
              />
            </div>
          )}

          {(isAllSelectVisible && options.length) ?
            <div
              className={`custom-control custom-checkbox mx-3 ${loading && 'disabled'}`}
              onClick={handleAllChecked}
            >
              <input
                type="checkbox"
                className="custom-control-input"
                checked={checkSubsetExistInArray(optionIds, selectedItemIds)}
                readOnly
              />
              <label className="custom-control-label">Select All</label>
            </div>
            : ''
          }
          {options.map(item => {
            const id = item[valueField]
            const isSelfPay = id === 'self_pay'
            const isSelfPaySelected = isPresentInArray(selectedItemIds, 'self_pay')
            const itemDisabled = loading || (!isSelfPay && isSelfPaySelected)

            return (
              <React.Fragment key={id}>
                <OptionRow
                  item={item}
                  checked={isPresentInArray(selectedItemIds, id)}
                  onClick={() => handleChecked(id)}
                  textField={textField}
                  beforeLabel={beforeLabel}
                  disabled={itemDisabled}
                />
                {isSelfPay && <hr className="my-1 mx-3" />}
              </React.Fragment>
            )
          })}
          {!options.length && <h6 className="w-100 text-center pb-5 pt-3">No Record Found</h6>}
          {(loadState.loading && options.length > 0) &&
            <div className="py-2 text-center loading text-primary font-weight-bold">
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> Loading...
            </div>
          }
        </div>
      </div>
      {touched &&
        ((error && <span className="invalid-feedback text-danger d-inline la-sm">{error}</span>) ||
          (warning && <span>{warning}</span>))}
    </div>
  )
}
